/**
 * PropertyPublicPage.jsx — Fiche publique partageable d'un bien (route /f/:token)
 * 
 * Référence mockup : /app/frontend/public/property_public_preview_v5.html
 * Endpoint backend : GET /api/public/property/{token} (sans auth)
 * 
 * RÈGLES STRICTES :
 *  - PAS de sidebar CRM, PAS de ModernTopBar (cette page est publique)
 *  - Aucun header Authorization dans les appels API
 *  - Aucune donnée confidentielle affichée
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { formatSurfaceFR } from '../utils/formatFR';
import { getActiviteMetier } from '../utils/propertyDataMapper';
import YouTubeEmbed from '../components/property/YouTubeEmbed';
import { agenceLogoUrl } from '../utils/branding';
import { getMediaUrl, getMediaSrcSet } from '../utils/mediaUrl';
import ProgressiveImage from '../components/shared/ProgressiveImage';
import { isLicenceAsset, isPartsSocialesAsset } from '../rules/propertyEditTabs';
import { derivePropertyFields } from '../utils/propertyDisplayFields';
import { ChiffresTableView, buildChiffresRows } from '../components/property/ChiffresTableView';
import { buildPropertyKpis, getMetierLabel, formatHonorairesChargeMention, KPI_GRID_CLASS } from '../utils/propertyKpis';
import { deriveHonorairesView, formatEuroFR, formatPercentFR, publicHonorairesMode, libellePrixTotal } from '../utils/honorairesModel';
import LocationMap from '../components/property/LocationMap';
import { renderDescriptionHtml } from '../utils/sanitizeHtml';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instance axios DÉDIÉE — sans les interceptors globaux qui injectent le JWT.
// Cette page est publique : aucun header Authorization ne doit être envoyé.
const publicClient = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Helpers
const fmtEuro = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (Number.isNaN(v)) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

const OPERATION_LABELS = {
  cession: 'Cession (PME/PMI & DAB)',
  vente_immo: 'Vente immobilière (murs)',
  vente_murs: 'Vente immobilière (murs)',  // Legacy alias
  vente_fonds: 'Cession (PME/PMI & DAB)',  // Legacy alias
  cession_bail: 'Cession de bail',  // Legacy : cession du droit au bail seul
  location: 'Location pure',
  location_droit_entree: 'Location + pas-de-porte',
  location_gerance: 'Location-gérance',
  parts_sociales: 'Cession de parts sociales',
  licence: 'Licence de débit de boissons',
};

// Détection famille d'opération pour mutualiser les KPIs (legacy + canoniques)
const isCessionFamily = (op) => ['cession', 'vente_fonds', 'cession_bail'].includes(op);
const isVenteImmoFamily = (op) => ['vente_immo', 'vente_murs'].includes(op);

// Format propre du sous-profil licence (licence_iv → "Licence IV")
const formatLicenceLabel = (s) => {
  if (!s) return null;
  const map = {
    licence_iv: 'Licence IV',
    licence_iii: 'Licence III',
    licence_restaurant: 'Licence Restaurant',
    licence_petite: 'Petite licence',
    licence_autre: 'Autre licence',
  };
  return map[s] || s;
};

// Visuel thématique (fallback licences/holdings)
const VISUAL_THEME_URL = {
  licence_iv: 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?auto=format&fit=crop&w=1600&q=80',
  licence_iii: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1600&q=80',
  licence_restaurant: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80',
  licence_petite: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=1600&q=80',
  licence_autre: 'https://images.unsplash.com/photo-1543007631-283050bb3e8c?auto=format&fit=crop&w=1600&q=80',
  ps_societe: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=1600&q=80',
  ps_holding: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80',
  ps_finance: 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?auto=format&fit=crop&w=1600&q=80',
};

const DPE_COLOR = { A: '#16A34A', B: '#84CC16', C: '#FACC15', D: '#F59E0B', E: '#F97316', F: '#EF4444', G: '#B91C1C' };

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${className}`}>
    {children}
  </span>
);

const Kpi = ({ icon, label, value, sub, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 transition hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
      <i className={`bi ${icon}`} />
      <span>{label}</span>
    </div>
    <div className="mt-1.5 text-slate-900 font-heading font-bold text-lg md:text-xl tracking-tight tabular-nums">
      {value || '—'}
    </div>
    {sub && (
      <div className="mt-0.5 text-slate-500 text-xs">
        {sub}
      </div>
    )}
  </div>
);

const PropertyPublicPage = () => {
  const { token } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Formulaire de contact
  const [contactForm, setContactForm] = useState({ nom: '', email: '', telephone: '', message: '', rgpd: false });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = fermé, sinon index photo

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        // IMPORTANT : publicClient est une instance axios dédiée qui n'embarque
        // pas les interceptors globaux → aucun header Authorization n'est injecté.
        const resp = await publicClient.get(`/public/property/${token}`);
        if (mounted) setProperty(resp.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || 'Bien introuvable ou lien expiré');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  // Soumission du formulaire de contact public → crée une demande dans contacts_agence
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError(null);
    if (!contactForm.rgpd) {
      setContactError("Veuillez accepter le traitement de vos données pour envoyer votre demande.");
      return;
    }
    setContactSubmitting(true);
    try {
      await publicClient.post(`/public/property/${token}/contact-request`, {
        nom: contactForm.nom.trim(),
        email: contactForm.email.trim(),
        telephone: contactForm.telephone.trim() || null,
        message: contactForm.message.trim(),
        rgpd_consent: contactForm.rgpd,
      });
      setContactSuccess(true);
      setContactForm({ nom: '', email: '', telephone: '', message: '', rgpd: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(' ; ')
        : (typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi. Réessayez ou contactez directement le négociateur.');
      setContactError(msg);
    } finally {
      setContactSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="public-loading">
        <div className="text-center">
          <i className="bi bi-arrow-repeat text-3xl text-blue-500 animate-spin" />
          <p className="mt-3 text-slate-500 text-sm">Chargement du bien…</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="public-error">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-2xl">
            <i className="bi bi-exclamation-triangle-fill" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Lien inaccessible</h1>
          <p className="text-slate-500 mt-2 text-sm">{error || 'Ce lien n\'est pas valide ou a été révoqué.'}</p>
          <a href="/" className="inline-block mt-5 text-blue-600 font-semibold text-sm hover:underline">
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }

  const {
    reference,
    operation,
    bien_general: bg = {},
    public_location: loc = {},
    informations_vente: vente = {},
    informations_bail: bail = {},
    composition: comp = {},
    description_detaillee,
    medias = {},
    agence,
    negociateur: nego,
  } = property;

  const photos = (medias.photos || []).filter(p => p.url);
  const mainPhoto = photos[0];
  const visualTheme = medias.visuel_thematique;
  const heroImage = mainPhoto?.url
    ? getMediaUrl(mainPhoto, 'medium', token)
    : (visualTheme && VISUAL_THEME_URL[visualTheme])
    || 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?auto=format&fit=crop&w=1600&q=80';

  const title = bg.titre_annonce || `${OPERATION_LABELS[operation] || 'Bien à la vente'}${loc.city_label ? ' — ' + loc.city_label : ''}`;
  const locationLabel = [loc.city_label, loc.sector_label].filter(Boolean).join(' — ') || 'Emplacement confidentiel';

  // ─────────────────────────────────────────────────────────────────────
  // Détection licence / parts sociales (actifs incorporels)
  // Doit être faite AVANT les KPIs pour que les KPIs licence soient prioritaires
  // sur les KPIs "location" génériques (qui sinon affichent DPE/Surface).
  // Source de vérité : taxonomie au ROOT du document (operation/objet/profil/sous_profil),
  // car le wizard et l'édition stockent ces champs au root, PAS dans bien_general.
  // ─────────────────────────────────────────────────────────────────────
  const taxonomyCtx = {
    operation: property.operation,
    objet: property.objet,
    profil: property.profil,
    sous_profil: property.sous_profil,
  };
  const isLicenceFlag = isLicenceAsset(taxonomyCtx);
  const isPartsFlag = isPartsSocialesAsset(taxonomyCtx);
  const isIncorporel = isLicenceFlag || isPartsFlag;

  // KPIs dynamiques selon opération
  const surface = comp.superficie_totale ? formatSurfaceFR(comp.superficie_totale) : null;

  // Libellé métier (KPI + badge hero) : source de vérité unique = getActiviteMetier
  // Règle Thie (17/07/2026) : afficher UNIQUEMENT l'activité, JAMAIS le profil en préfixe.
  // Ordre déjà géré par getActiviteMetier : activite_precise → activite_autre (autre_*) → sous_profil label → profil label.
  // → règle aussi le doublon "Commerce de détail · Commerce de détail (retail)" (plus de concat).
  const metierLabel = getMetierLabel(property);

  // iter116 — KPI de tête : SOURCE UNIQUE partagée avec la fiche consultation et le PDF
  // (`utils/propertyKpis.js`). La fiche publique ne recalcule RIEN.
  const kpis = buildPropertyKpis(property, { publicView: true });

  // Honoraires : source unique `utils/honorairesModel.js`. La fiche publique ne
  // recalcule rien, elle applique la règle d'affichage selon QUI PAIE.
  const honoraires = deriveHonorairesView(property);
  const honorairesMention = formatHonorairesChargeMention(property);
  const honorairesMode = publicHonorairesMode(honoraires, isLicenceAsset({
    operation: property.operation, objet: property.objet, profil: property.profil, sous_profil: property.sous_profil,
  }));
  const baremeUrl = agence?.bareme_honoraires_url || null;

  const caracteristiques = [
    { label: 'Surface totale', value: surface },
    { label: 'Surface vente', value: comp.surface_vente ? formatSurfaceFR(comp.surface_vente) : null },
    { label: 'Cuisine', value: comp.surface_cuisine ? formatSurfaceFR(comp.surface_cuisine) : null },
    { label: 'Réserve', value: comp.reserve ? formatSurfaceFR(comp.reserve) : null },
    { label: 'Terrasse', value: comp.terrasse_m2 ? formatSurfaceFR(comp.terrasse_m2) : null },
    { label: 'Couverts', value: comp.nombre_couverts_total || null },
    { label: 'Hauteur plafond', value: comp.hauteur_plafond ? `${comp.hauteur_plafond} m` : null },
    { label: 'Façade', value: comp.facade ? `${comp.facade} m` : null },
    { label: 'Étage', value: comp.etage || null },
    { label: 'Vitrine', value: comp.vitrine || null },
    { label: 'Extraction', value: comp.extraction ? 'Oui' : null },
    { label: 'Climatisation', value: comp.climatisation ? 'Oui' : null },
    { label: 'PMR', value: comp.pmr ? 'Oui' : null },
    { label: 'Chambre froide', value: comp.chambre_froide ? 'Oui' : null },
  ].filter(c => c.value);

  // Dérivation centralisée (parité avec la vue interne, filtrée par activité,
  // sans aucun champ confidentiel) — source de vérité unique.
  const derived = derivePropertyFields(property);
  const hasAnyCarac = derived.surfaces.length > 0 || derived.structurelles.length > 0
    || derived.capacites.length > 0 || derived.equipements.length > 0 || !!derived.specificites;

  const showBail = !isIncorporel && ['cession', 'vente_fonds', 'cession_bail', 'location', 'location_droit_entree', 'location_gerance', 'vente_immo', 'vente_murs'].includes(operation);
  const showDiagnostics = !isIncorporel && (bg.dpe || bg.ges);

  // Compat titre_locatif : peut être string (legacy) ou objet TitreLocatif (cible)
  const titreLocatifType = typeof bail.titre_locatif === 'string'
    ? bail.titre_locatif
    : (bail.titre_locatif?.type || null);
  const titreLocatifDuree = typeof bail.titre_locatif === 'object' && bail.titre_locatif !== null
    ? bail.titre_locatif?.duree
    : null;
  const dureeAffichee = titreLocatifDuree || bail.duree_gerance || null;
  const chargesMois = bail.charges_mensuelles != null ? bail.charges_mensuelles : (bail.charges ?? null);

  // lot2 — mêmes lignes, même rendu que la fiche interne (composant partagé). Aucun repli
  // legacy ici : la fiche publique ne voit que ce que la projection publique a laissé passer.
  const chiffresRows = buildChiffresRows(property.exploitation && property.exploitation.chiffres);
  const hasEffectif = !!(property.effectif
    && (property.effectif.effectif_salaries != null || property.effectif.detail_salaries));
  const hasCommissions = derived.commissions.length > 0;
  // lot2 (2b) — « pas de section vide » s'applique AUSSI au menu : un onglet ne s'affiche
  // que si sa section a du contenu (le tabac affichait « Bail » vers une ancre vide).
  // lot H — un bien dont seuls le loyer annuel, l'impôt foncier ou une redevance sont
  // renseignés a du contenu de bail : la section ne doit pas rester masquée.
  const hasBailContent = showBail && !!(titreLocatifType || bail.loyer_mensuel || dureeAffichee
    || bail.droit_entree || bail.date_echeance_bail || bail.date_debut_bail
    || bail.loyer_annuel != null || bail.impot_foncier != null || bail.loyer_soumis_tva
    || bail.revision_loyer || bail.depot_garantie != null || chargesMois != null
    || bail.redevance_mensuelle != null || bail.redevance_annuelle != null || bail.duree_gerance);

  const negoInitials = nego
    ? `${(nego.prenom || '').charAt(0)}${(nego.nom || '').charAt(0)}`.toUpperCase() || 'AG'
    : 'AG';
  const negoFullName = nego ? `${nego.prenom || ''} ${nego.nom || ''}`.trim() || 'Votre négociateur' : 'Votre négociateur';
  const qrCode = property.qr_code;
  const shareUrl = property.share_url;

  return (
    <div className="min-h-screen bg-slate-50 font-body" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="public-property-page">
      {/* Google Fonts — preload pour cohérence visuelle */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Outfit:wght@500;600;700;800;900&display=swap');`}</style>

      {/* ═════════ TOPBAR PUBLIQUE ═════════ */}
      <div className="no-print sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-20 flex items-center gap-3">
          <div className="flex items-center gap-2">
            {agence?.logo_url ? (
              <img src={agenceLogoUrl(agence.logo_url)} alt={agence.nom} className="h-[60px] w-auto object-contain" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white">
                <i className="bi bi-building" />
              </div>
            )}
            {!agence?.logo_url && (
              <div className="leading-tight">
                <div className="font-heading font-bold text-sm text-slate-900">{agence?.nom || 'Komerces'}</div>
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="public-print-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition"
            >
              <i className="bi bi-printer" />
              <span className="hidden sm:inline">Imprimer</span>
            </button>
            <button
              onClick={handleShareCopy}
              data-testid="public-share-btn"
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg transition"
            >
              <i className={`bi ${copied ? 'bi-check-circle-fill text-emerald-500' : 'bi-share'}`} />
              {copied ? 'Lien copié !' : 'Partager'}
            </button>
            {nego?.email && (
              <a
                href="#sec-contact-form"
                data-testid="public-contact-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition"
              >
                <i className="bi bi-chat-dots-fill" />
                <span className="hidden sm:inline">Me contacter</span>
              </a>
            )}
          </div>
        </div>
        {/* TOC pills - masqué sur mobile (la page se scrolle naturellement, évite scroll horizontal moche) */}
        <nav className="border-t border-slate-100 no-print hidden md:block">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex items-center gap-1.5 overflow-x-auto">
            <a href="#sec-apercu" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-eye" />Aperçu</a>
            {/* lot2 (2b) — un onglet ne pointe JAMAIS vers une section vide : chaque pastille
                partage exactement la condition de rendu de sa section. */}
            {hasAnyCarac && <a href="#sec-carac" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-list-check" />Caractéristiques</a>}
            {hasBailContent && <a href="#sec-bail" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-file-earmark-text" />Bail</a>}
            {chiffresRows.length > 0 && <a href="#sec-chiffres" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-bar-chart" />Chiffres</a>}
            <a href="#sec-loc" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-geo-alt" />Localisation</a>
            {photos.length > 0 && <a href="#sec-photos" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-images" />Photos</a>}
            {(property.medias?.video_url || property.video_url) && <a href="#sec-video" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-play-btn" />Vidéo</a>}
            <a href="#sec-contact" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 whitespace-nowrap transition"><i className="bi bi-person" />Contact</a>
          </div>
        </nav>
      </div>

      {/* ═════════ HERO ═════════ */}
      <section id="sec-apercu" className="relative">
        <div
          className="h-[300px] md:h-[420px] relative bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(135deg,rgba(11,18,32,.1),rgba(11,18,32,.55)),url('${heroImage}')`,
          }}
          data-testid="public-hero"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
          <div className="relative max-w-6xl mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-20 md:pb-24">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Chip className="bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Disponible
              </Chip>
              <Chip className="bg-white/10 text-white/90 ring-1 ring-white/20 backdrop-blur">
                {OPERATION_LABELS[operation] || 'Bien'}
              </Chip>
            </div>
            <h1
              className="text-white font-heading text-2xl md:text-5xl font-extrabold leading-tight max-w-3xl break-words [overflow-wrap:anywhere]"
              style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}
              data-testid="public-title"
            >
              {title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-white/80 text-xs md:text-sm">
              <span className="inline-flex items-center gap-1.5"><i className="bi bi-geo-alt-fill" />{locationLabel}</span>
              {surface && <span className="inline-flex items-center gap-1.5"><i className="bi bi-rulers" />{surface}</span>}
              {reference && <span className="inline-flex items-center gap-1.5"><i className="bi bi-hash" />{reference}</span>}
            </div>
          </div>
        </div>

        {/* Thumbnails photos (desktop only) */}
        {photos.length > 1 && (
          <div className="max-w-6xl mx-auto px-4 md:px-8 -mt-10 relative z-10 hidden md:grid grid-cols-4 gap-3 no-print">
            {photos.slice(0, 4).map((p, i) => {
              const isLast = i === 3 && photos.length > 4;
              return (
                <button
                  key={p.id || i}
                  onClick={() => setLightboxIndex(i)}
                  type="button"
                  className="aspect-square rounded-xl ring-4 ring-white shadow-lg relative overflow-hidden hover:opacity-90 transition cursor-pointer"
                  aria-label={`Voir photo ${i + 1}`}
                  data-testid={`public-hero-thumb-${i}`}
                >
                  <ProgressiveImage
                    src={getMediaUrl(p, 'medium', token)}
                    lowSrc={getMediaUrl(p, 'thumb', token)}
                    srcSet={getMediaSrcSet(p, token)}
                    sizes="(max-width: 1024px) 50vw, 25vw"
                    alt=""
                    className="w-full h-full object-cover"
                    containerClassName="absolute inset-0"
                  />
                  {isLast && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-sm z-10">
                      <i className="bi bi-images mr-1" />+{photos.length - 4} photos
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ═════════ KPIs ═════════
          Rangée adaptative : 2 à 4 KPI selon l'opération, jamais de case vide. */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-8 md:mt-12">
        <div className={`grid gap-3 md:gap-4 ${KPI_GRID_CLASS[kpis.length] || KPI_GRID_CLASS[4]}`} data-testid="public-kpis">
          {kpis.map((k, i) => (
            <Kpi key={i} icon={k.icon} label={k.label} value={k.value} sub={k.sub} testId={`public-kpi-${i}`} />
          ))}
        </div>

        {/* ═══ HONORAIRES — règle d'affichage client (iter124) ═══
            Vente charge ACQUÉREUR  → mention ici, décomposition en bas de colonne droite
            Vente charge VENDEUR / PARTAGÉS → mention seule + lien barème
            LOCATION / LICENCE      → montant des honoraires + charge + lien barème, sans ventilation
            Charge NON renseignée   → rien (le prix FAI se suffit) */}
        {honorairesMode !== 'none' && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600" data-testid="public-honoraires-mention">
            <span className="inline-flex items-center gap-2">
              <i className="bi bi-receipt text-slate-400" />
              <span className="font-semibold text-slate-700" data-testid="public-honoraires-mention-text">
                {honorairesMode === 'montant' && honoraires.ttc != null
                  ? `Honoraires ${formatEuroFR(honoraires.ttc)} TTC${honorairesMention ? ` ${honorairesMention.replace(/^Honoraires /, '')}` : ''}`
                  : honorairesMention}
              </span>
            </span>
            {baremeUrl && (
              <a href={baremeUrl} target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline font-semibold" data-testid="public-bareme-link">
                Barème d'honoraires du cabinet
              </a>
            )}
          </div>
        )}
      </section>


      {/* ═════════ CONTENT GRID ═════════ */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {bg.description_courte && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 text-left">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit',sans-serif" }}>Le bien en un coup d'œil</h2>
              <p className="text-slate-700 leading-relaxed text-[15px]">{bg.description_courte}</p>
            </article>
          )}

          {description_detaillee && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 text-left">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit',sans-serif" }}>Description détaillée</h2>
              <div
                className="prose prose-sm max-w-none text-slate-700 leading-relaxed text-[14.5px] text-left [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>h3]:font-bold [&>h3]:text-base"
                dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(description_detaillee) }}
              />
            </article>
          )}

          {hasAnyCarac && (
            <article id="sec-carac" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="public-caracteristiques">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Caractéristiques</h2>

              {derived.surfaces.length > 0 && (
                <div className="mb-6" data-testid="public-carac-surfaces">
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Surfaces</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {derived.surfaces.map((s, i) => (
                      <div key={i} className={`rounded-lg p-3 ${s.highlight ? 'bg-blue-50 ring-1 ring-blue-200' : s.accent === 'emerald' ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-slate-50'}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                        <div className={`text-lg font-bold mt-0.5 ${s.highlight ? 'text-blue-700' : s.accent === 'emerald' ? 'text-emerald-700' : 'text-slate-900'}`} style={{ fontFamily: "'Outfit',sans-serif" }}>
                          {formatSurfaceFR(s.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {derived.structurelles.length > 0 && (
                <div className="mb-6" data-testid="public-carac-structurelles">
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Caractéristiques structurelles</h3>
                  <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {derived.structurelles.map((c, i) => (
                      <div key={i}>
                        <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</dt>
                        <dd className="font-semibold text-slate-900 mt-0.5">{c.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {derived.capacites.length > 0 && (
                <div className="mb-6" data-testid="public-carac-capacites">
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Capacités</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {derived.capacites.map((c, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
                        <div className="text-2xl font-bold mt-0.5 text-slate-900" style={{ fontFamily: "'Outfit',sans-serif" }}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {derived.equipements.length > 0 && (
                <div data-testid="public-carac-equipements">
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Équipements &amp; options</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 grid-flow-row-dense gap-x-4 gap-y-2 text-sm">
                    {[...derived.equipements.filter((e) => !e.quals), ...derived.equipements.filter((e) => e.quals)].map((e, i) => (
                      <div key={i} className={`flex items-center gap-2 ${e.quals ? 'col-span-2 md:col-span-3 flex-wrap gap-y-1.5' : (e.text && e.label.length + e.text.length > 24 ? 'col-span-2' : '')}`}>
                        {e.quals ? (
                          <>
                            <i className="bi bi-dot text-slate-400" />
                            <span className="text-slate-500 shrink-0 whitespace-nowrap">{e.label} :</span>
                            {e.quals.map((q, j) => (
                              <Chip key={j} className="bg-slate-100 text-slate-600">{q}</Chip>
                            ))}
                          </>
                        ) : e.text ? (
                          <>
                            <i className="bi bi-dot text-slate-400" />
                            <span className="text-slate-500 min-w-0 break-words">{e.label} :</span>
                            <span className="font-semibold text-slate-900 min-w-0 break-words">{e.text}</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle-fill text-slate-500" />
                            <span className="font-medium text-slate-700">{e.label}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Spécificités d'exploitation (texte libre whitelisté) — retours à la ligne respectés */}
              {derived.specificites && (
                <div className="mt-6" data-testid="public-carac-specificites">
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Spécificités</h3>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line text-left" data-testid="public-specificites-text">
                    {derived.specificites}
                  </p>
                </div>
              )}

              {showDiagnostics && (
                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Diagnostics</span>
                  {bg.dpe && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-slate-600">DPE</span>
                      <span className="px-2 py-1 rounded-md text-white font-bold text-xs" style={{ background: DPE_COLOR[bg.dpe] || '#94A3B8' }}>
                        {bg.dpe}
                      </span>
                    </span>
                  )}
                  {bg.ges && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-slate-600">GES</span>
                      <span className="px-2 py-1 rounded-md text-white font-bold text-xs" style={{ background: DPE_COLOR[bg.ges] || '#94A3B8' }}>
                        {bg.ges}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </article>
          )}

          {/* iter132 — Effectif : présent uniquement si l'agence a armé l'interrupteur
              (le backend ne projette `effectif` que dans ce cas). */}
          {hasEffectif && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="public-section-effectif">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Effectif</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {property.effectif.effectif_salaries != null && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Salariés</div>
                    <div className="text-lg font-bold mt-0.5 text-slate-900 tabular-nums" style={{ fontFamily: "'Outfit',sans-serif" }} data-testid="public-effectif-salaries">
                      {property.effectif.effectif_salaries}
                    </div>
                  </div>
                )}
                {property.effectif.detail_salaries && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Détail de l&apos;effectif</div>
                    <div className="text-sm font-semibold mt-0.5 text-slate-800 whitespace-pre-line" data-testid="public-effectif-detail">
                      {property.effectif.detail_salaries}
                    </div>
                  </div>
                )}
              </div>
            </article>
          )}

          {/* lot1 — Commissions (tabac / presse / Loto-FDJ / PMU) : bloc dédié, jamais
              mélangé aux capacités (un montant en euros n'est pas une capacité). */}
          {hasCommissions && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="public-section-commissions">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Commissions</h2>
              <dl className="divide-y divide-slate-100 text-sm">
                {derived.commissions.map((c) => (
                  <div key={c.label} className="py-3 flex justify-between gap-4">
                    <dt className="text-slate-500">{c.label}</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{c.value}</dd>
                  </div>
                ))}
              </dl>
            </article>
          )}

          {hasBailContent && (
            <article id="sec-bail" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="public-bail">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Bail &amp; éléments juridiques</h2>
              <dl className="divide-y divide-slate-100 text-sm">
                {titreLocatifType && <div className="py-3 flex justify-between"><dt className="text-slate-500">Type de bail</dt><dd className="font-semibold text-slate-900">{titreLocatifType}</dd></div>}
                {dureeAffichee && <div className="py-3 flex justify-between"><dt className="text-slate-500">Durée</dt><dd className="font-semibold text-slate-900">{dureeAffichee}</dd></div>}
                {/* lot H — la date de début situe l'échéance dans le cycle 3/6/9 : sans elle,
                    un repreneur ne sait pas où il se trouve dans le bail. */}
                {bail.date_debut_bail && (
                  <div className="py-3 flex justify-between" data-testid="public-bail-debut">
                    <dt className="text-slate-500">Date de début</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{new Date(bail.date_debut_bail).toLocaleDateString('fr-FR')}</dd>
                  </div>
                )}
                {/* iter133 — la date d'échéance est LA donnée qu'un repreneur regarde en premier :
                    un bail à 2 ans du terme ne vaut pas un bail à 7 ans. */}
                {bail.date_echeance_bail && (
                  <div className="py-3 flex justify-between" data-testid="public-bail-echeance">
                    <dt className="text-slate-500">Date d&apos;échéance</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{new Date(bail.date_echeance_bail).toLocaleDateString('fr-FR')}</dd>
                  </div>
                )}
                {bail.loyer_mensuel != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Loyer mensuel HT</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.loyer_mensuel)}</dd></div>}
                {/* lot H — 6 champs déjà exposés par la projection publique (server.py, « iter130 —
                    champs de bail légitimement PUBLICS ») et jamais affichés. Le plus important :
                    la TVA sur le loyer, qui fait varier le coût réel de 20 %. */}
                {bail.loyer_annuel != null && <div className="py-3 flex justify-between" data-testid="public-bail-loyer-annuel"><dt className="text-slate-500">Loyer annuel HT</dt><dd className="font-semibold text-slate-900 tabular-nums">{fmtEuro(bail.loyer_annuel)}</dd></div>}
                {chargesMois != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Charges /mois</dt><dd className="font-semibold text-slate-900">{fmtEuro(chargesMois)}</dd></div>}
                {bail.loyer_soumis_tva != null && bail.loyer_soumis_tva !== '' && (
                  <div className="py-3 flex justify-between" data-testid="public-bail-tva">
                    <dt className="text-slate-500">Loyer soumis à TVA</dt>
                    <dd className="font-semibold text-slate-900">{typeof bail.loyer_soumis_tva === 'boolean' ? (bail.loyer_soumis_tva ? 'Oui' : 'Non') : bail.loyer_soumis_tva}</dd>
                  </div>
                )}
                {bail.revision_loyer && <div className="py-3 flex justify-between" data-testid="public-bail-revision"><dt className="text-slate-500">Révision du loyer</dt><dd className="font-semibold text-slate-900">{bail.revision_loyer}</dd></div>}
                {bail.impot_foncier != null && <div className="py-3 flex justify-between" data-testid="public-bail-impot-foncier"><dt className="text-slate-500">Impôt foncier</dt><dd className="font-semibold text-slate-900 tabular-nums">{fmtEuro(bail.impot_foncier)}</dd></div>}
                {bail.depot_garantie != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Dépôt de garantie</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.depot_garantie)}</dd></div>}
                {bail.droit_entree != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Pas-de-porte</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.droit_entree)}</dd></div>}
                {bail.redevance_mensuelle != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Redevance /mois</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.redevance_mensuelle)}</dd></div>}
                {bail.redevance_annuelle != null && <div className="py-3 flex justify-between" data-testid="public-bail-redevance-annuelle"><dt className="text-slate-500">Redevance /an</dt><dd className="font-semibold text-slate-900 tabular-nums">{fmtEuro(bail.redevance_annuelle)}</dd></div>}
                {bail.duree_gerance && <div className="py-3 flex justify-between" data-testid="public-bail-duree-gerance"><dt className="text-slate-500">Durée de gérance</dt><dd className="font-semibold text-slate-900">{bail.duree_gerance}</dd></div>}
              </dl>
            </article>
          )}

          {/* lot2 — MÊME tableau que la fiche interne : composant partagé `ChiffresTableView`
              (années en colonnes, EBE et résultat net sur 3 exercices, ligne vide masquée).
              Le backend strippe déjà `exploitation.chiffres` quand l'interrupteur
              `afficher_ca_public` est false → si des lignes existent ici, on peut afficher. */}
          {chiffresRows.length > 0 && (
            <article id="sec-chiffres" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="public-section-chiffres">
              <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Chiffres</h2>
              <ChiffresTableView rows={chiffresRows} testId="public-perf-table" />
            </article>
          )}

          <article id="sec-loc" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32">
            <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Où se situe le bien</h2>
            {(loc.city_label || loc.sector_label || (loc.zone_lat != null && loc.zone_lon != null)) ? (
              <LocationMap
                ville={loc.city_label}
                secteur={loc.sector_label}
                approxLat={loc.zone_lat}
                approxLon={loc.zone_lon}
                isConfidential={false}
              />
            ) : (
              <div className="aspect-[16/9] rounded-lg bg-slate-100 flex items-center justify-center">
                <div className="text-center">
                  <i className="bi bi-geo-alt text-4xl text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600 font-semibold">{locationLabel}</p>
                  {loc.emplacement_note && <p className="text-xs text-slate-500 mt-1">{loc.emplacement_note}</p>}
                </div>
              </div>
            )}
            {loc.emplacement_note && (loc.city_label || loc.sector_label) && (
              <p className="text-xs text-slate-500 mt-3">{loc.emplacement_note}</p>
            )}
            <p className="text-xs text-slate-400 italic mt-3">
              <i className="bi bi-info-circle" /> L'adresse exacte sera communiquée après signature d'un engagement de confidentialité.
            </p>
          </article>
        </div>

        {/* ═════════ SIDEBAR CONTACT (avec formulaire intégré) ═════════ */}
        <aside id="sec-contact" className="space-y-4 scroll-mt-32">
          {/* Carte négociateur */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="public-contact-card">
            <div className="flex items-center gap-3">
              {nego?.photo_url ? (
                <img src={nego.photo_url} alt={negoFullName} className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                  {negoInitials}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Votre interlocuteur</div>
                <div className="font-bold text-slate-900">{negoFullName}</div>
                {agence?.nom && <div className="text-xs text-slate-500">{agence.nom}</div>}
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {nego?.telephone && (
                <a href={`tel:${nego.telephone}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-blue-600 transition" data-testid="public-nego-phone">
                  <i className="bi bi-telephone-fill text-blue-500" />
                  {nego.telephone}
                </a>
              )}
              {nego?.email && (
                <a href={`mailto:${nego.email}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-blue-600 transition break-all" data-testid="public-nego-email">
                  <i className="bi bi-envelope-fill text-blue-500" />
                  {nego.email}
                </a>
              )}
            </div>
            <a
              href="#sec-contact-form"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm shadow-sm transition"
              data-testid="public-rdv-btn"
            >
              <i className="bi bi-chat-dots-fill" />Demander un rendez-vous
            </a>
          </div>

          {/* ═════════ FORMULAIRE DE CONTACT (intégré dans la sidebar) ═════════ */}
          <div id="sec-contact-form" className="bg-white rounded-2xl border border-slate-200 p-5 scroll-mt-32" data-testid="public-contact-form-section">
            <h2 className="font-heading text-base font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit',sans-serif" }}>
              Intéressé(e) par ce bien ?
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              {nego?.prenom ? `Laissez vos coordonnées, ${nego.prenom} vous recontactera.` : 'Le négociateur vous recontactera rapidement.'}
            </p>

            {contactSuccess ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="public-contact-success">
                <div className="w-10 h-10 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-2">
                  <i className="bi bi-check-circle-fill text-xl" />
                </div>
                <p className="font-bold text-emerald-900 text-sm">Demande envoyée !</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Le négociateur vous recontactera très prochainement.
                </p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-3" data-testid="public-contact-form">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Nom complet <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                    value={contactForm.nom}
                    onChange={(e) => setContactForm({ ...contactForm, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Jean Dupont"
                    data-testid="public-contact-input-nom"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Email <span className="text-rose-500">*</span></label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="jean.dupont@email.com"
                    data-testid="public-contact-input-email"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Téléphone <span className="text-slate-400 font-normal">(opt.)</span></label>
                  <input
                    type="tel"
                    value={contactForm.telephone}
                    onChange={(e) => setContactForm({ ...contactForm, telephone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="06 12 34 56 78"
                    data-testid="public-contact-input-telephone"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Message <span className="text-rose-500">*</span></label>
                  <textarea
                    required
                    minLength={5}
                    maxLength={2000}
                    rows={3}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Bonjour, je souhaite obtenir plus d'informations…"
                    data-testid="public-contact-input-message"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">{contactForm.message.length} / 2000</p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={contactForm.rgpd}
                    onChange={(e) => setContactForm({ ...contactForm, rgpd: e.target.checked })}
                    className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="public-contact-input-rgpd"
                  />
                  <span className="text-[11px] text-slate-600 leading-snug">
                    J'accepte que mes données soient transmises à <strong>{agence?.nom || 'l\'agence'}</strong>. <span className="text-rose-500">*</span>
                  </span>
                </label>

                {contactError && (
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-700" data-testid="public-contact-error">
                    <i className="bi bi-exclamation-triangle-fill mr-1" />{contactError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={contactSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm shadow-sm transition"
                  data-testid="public-contact-submit"
                >
                  {contactSubmitting ? (
                    <><i className="bi bi-arrow-repeat animate-spin" />Envoi…</>
                  ) : (
                    <><i className="bi bi-send-fill" />Envoyer ma demande</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* ═════════ QR CODE PARTAGE (sidebar publique) ═════════ */}
          {qrCode && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="public-qr-section">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 bg-slate-50 p-2 rounded-lg border border-slate-200">
                  <img src={qrCode} alt="QR Code de cette fiche" className="w-20 h-20 block" data-testid="public-qr-image" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1.5" style={{ fontFamily: "'Outfit',sans-serif" }}>
                    <i className="bi bi-qr-code text-slate-700"></i>Partager cette fiche
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Scannez ou montrez le QR à un proche pour partager le lien.
                  </p>
                  {shareUrl && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(shareUrl); }}
                      className="mt-2 text-[11px] text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
                      data-testid="public-qr-copy-link"
                    >
                      <i className="bi bi-clipboard"></i>Copier le lien
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {agence && (
            <div className="bg-slate-900 rounded-2xl text-white p-5" data-testid="public-agency-dark-card">
              {(() => {
                const _normUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
                const _wrap = (inner) => agence.website ? (
                  <a href={_normUrl(agence.website)} target="_blank" rel="noopener noreferrer"
                     className="inline-flex transition-opacity hover:opacity-80" data-testid="public-agency-logo-link"
                     title={`Visiter le site de ${agence.nom || "l'agence"}`}>
                    {inner}
                  </a>
                ) : inner;

                if (agence.logo_url_dark) {
                  return (
                    <div className="flex items-center justify-center py-2">
                      {_wrap(
                        <img src={agenceLogoUrl(agence.logo_url_dark)} alt={agence.nom}
                             className="h-20 w-auto max-w-full object-contain" />
                      )}
                    </div>
                  );
                }
                if (agence.logo_url) {
                  return (
                    <div className="flex items-center justify-center py-2">
                      {_wrap(
                        <div className="bg-white rounded-xl px-4 py-3 inline-flex items-center justify-center">
                          <img src={agenceLogoUrl(agence.logo_url)} alt={agence.nom}
                               className="h-16 w-auto max-w-[220px] object-contain" />
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shrink-0">
                      <i className="bi bi-building" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold truncate">{agence.nom}</div>
                      {agence.ville && <div className="text-[11px] text-white/60 truncate">{agence.ville}</div>}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const _normUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
                const socials = [
                  { key: 'website', url: agence.website, icon: 'bi-globe', label: 'Site web' },
                  { key: 'instagram', url: agence.instagram, icon: 'bi-instagram', label: 'Instagram' },
                  { key: 'facebook', url: agence.facebook, icon: 'bi-facebook', label: 'Facebook' },
                  { key: 'linkedin', url: agence.linkedin, icon: 'bi-linkedin', label: 'LinkedIn' },
                ].filter((s) => s.url);
                if (!socials.length) return null;
                return (
                  <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/10" data-testid="public-agency-socials">
                    {socials.map((s) => (
                      <a
                        key={s.key}
                        href={_normUrl(s.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={s.label}
                        aria-label={s.label}
                        data-testid={`public-agency-social-${s.key}`}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/90 hover:text-white transition-colors"
                      >
                        <i className={`bi ${s.icon} text-base`} />
                      </a>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* iter124 — décomposition des honoraires : UNIQUEMENT vente/cession avec
              honoraires à la charge de l'acquéreur, en bas de la colonne de droite. */}
          {honorairesMode === 'detail' && (honoraires.ttc != null || honoraires.pct != null) && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="public-honoraires-detail">
              <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">
                Détail des honoraires
              </h3>
              <dl className="space-y-2 text-sm">
                {honoraires.pnv != null && (
                  <div className="flex justify-between gap-3" data-testid="public-honoraires-pnv">
                    <dt className="text-slate-500">Prix net vendeur</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{formatEuroFR(honoraires.pnv)}</dd>
                  </div>
                )}
                {honoraires.ht != null && (
                  <div className="flex justify-between gap-3" data-testid="public-honoraires-ht">
                    <dt className="text-slate-500">Honoraires HT</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{formatEuroFR(honoraires.ht)}</dd>
                  </div>
                )}
                {honoraires.tva_montant != null && (
                  <div className="flex justify-between gap-3" data-testid="public-honoraires-tva">
                    <dt className="text-slate-500">TVA ({honoraires.tva_taux}&nbsp;%)</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{formatEuroFR(honoraires.tva_montant)}</dd>
                  </div>
                )}
                {honoraires.ttc != null && (
                  <div className="flex justify-between gap-3" data-testid="public-honoraires-ttc">
                    <dt className="text-slate-500">Honoraires TTC</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{formatEuroFR(honoraires.ttc)}</dd>
                  </div>
                )}
                {honoraires.pct != null && (
                  <div className="flex justify-between gap-3" data-testid="public-honoraires-taux">
                    <dt className="text-slate-500">Taux ({honoraires.pctBaseLabel})</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{formatPercentFR(honoraires.pct)}</dd>
                  </div>
                )}
                {honoraires.fai != null && (
                  <div className="flex justify-between gap-3 pt-2 border-t border-slate-200" data-testid="public-honoraires-fai">
                    <dt className="text-slate-700 font-semibold">{libellePrixTotal(honoraires.charge)}</dt>
                    <dd className="font-bold text-slate-900 tabular-nums">{formatEuroFR(honoraires.fai)}</dd>
                  </div>
                )}
              </dl>
              {baremeUrl && (
                <a href={baremeUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-block text-xs text-blue-600 hover:underline font-semibold"
                  data-testid="public-bareme-link-detail">
                  Barème d'honoraires du cabinet
                </a>
              )}
            </div>
          )}
        </aside>
      </section>

      {/* ═════════ PHOTOS ═════════ */}
      {photos.length > 1 && (
        <section id="sec-photos" className="max-w-6xl mx-auto px-4 md:px-8 mt-12 scroll-mt-32">
          <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Photos du bien</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <button
                key={p.id || i}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="aspect-square rounded-xl block hover:opacity-90 transition cursor-pointer relative overflow-hidden bg-slate-100"
                aria-label={p.name || `Voir photo ${i + 1}`}
                data-testid={`public-photo-${i}`}
              >
                <ProgressiveImage
                  src={getMediaUrl(p, 'medium', token)}
                  lowSrc={getMediaUrl(p, 'thumb', token)}
                  srcSet={getMediaSrcSet(p, token)}
                  sizes="(max-width: 768px) 50vw, 25vw"
                  alt=""
                  className="w-full h-full object-cover"
                  containerClassName="absolute inset-0"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ═════════ VIDÉO YOUTUBE (click-to-play, RGPD-friendly) ═════════ */}
      {(() => {
        const videoUrl = property.medias?.video_url || property.video_url;
        if (!videoUrl) return null;
        return (
          <section id="sec-video" className="max-w-6xl mx-auto px-4 md:px-8 mt-12 scroll-mt-32">
            <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit',sans-serif" }}>Vidéo de présentation</h2>
            <YouTubeEmbed
              url={videoUrl}
              title={property.titre_annonce || 'Vidéo du bien'}
              testId="public-video"
            />
          </section>
        );
      })()}

      {/* ═════════ LIGHTBOX PLEIN ÉCRAN (style Airbnb) ═════════ */}
      {lightboxIndex !== null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          shareToken={token}
          onClose={() => setLightboxIndex(null)}
          onChange={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}

      {/* ═════════ FOOTER ═════════ */}
      <footer className="max-w-6xl mx-auto px-4 md:px-8 mt-16 mb-24 lg:mb-10 text-center text-xs text-slate-500">
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6" />
        {reference && (
          <p>
            Référence <span className="font-mono font-semibold">{reference}</span>
            {' '}· Édité le {new Date().toLocaleDateString('fr-FR')}
          </p>
        )}
        <p className="mt-1">
          Informations non contractuelles. Dossier complet disponible après signature d'un engagement de confidentialité.
        </p>
        <p className="mt-3 font-semibold">© {new Date().getFullYear()} {agence?.nom || 'Komerces'} — Propulsé par Komerces</p>
      </footer>

      {/* ═════════ BOTTOM STICKY CTA MOBILE ═════════ */}
      {(nego?.telephone || nego?.email) && (
        <div className="lg:hidden no-print fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.05)]">
          <div className="px-3 py-2.5 flex items-center gap-2">
            {nego?.telephone && (
              <a
                href={`tel:${nego.telephone}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-semibold text-slate-700 transition"
                data-testid="public-sticky-call"
              >
                <i className="bi bi-telephone-fill text-emerald-600" />Appeler
              </a>
            )}
            {nego?.email && (
              <a
                href={`mailto:${nego.email}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-semibold text-slate-700 transition"
                data-testid="public-sticky-email"
              >
                <i className="bi bi-envelope-fill text-blue-500" />Email
              </a>
            )}
            {nego?.email && (
              <a
                href="#sec-contact-form"
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition"
                data-testid="public-sticky-rdv"
              >
                <i className="bi bi-calendar-check-fill" />RDV
              </a>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
};

export default PropertyPublicPage;

// =============================================================================
// PHOTO LIGHTBOX — Galerie plein écran style Airbnb
// =============================================================================
// Features :
//  - Backdrop noir, photo centrée
//  - Boutons précédent/suivant (desktop)
//  - Swipe gestures (mobile, threshold 50px)
//  - Compteur "X / N"
//  - Bouton fermer (X)
//  - Tap hors photo = ferme
//  - Esc = ferme, ←/→ navigation clavier
//  - Préchargement des photos voisines pour transitions instantanées
// =============================================================================
const PhotoLightbox = ({ photos, index, shareToken, onClose, onChange }) => {
  const total = photos.length;
  const photo = photos[index];
  const url = getMediaUrl(photo, 'large', shareToken);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const goPrev = React.useCallback(() => {
    onChange(index === 0 ? total - 1 : index - 1);
  }, [index, total, onChange]);

  const goNext = React.useCallback(() => {
    onChange(index === total - 1 ? 0 : index + 1);
  }, [index, total, onChange]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handler);
    // Prevent scroll while lightbox is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  // Préchargement des photos voisines
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const idx = (i + total) % total;
      const p = photos[idx];
      if (!p) return;
      const u = getMediaUrl(p, 'large', shareToken);
      if (!u) return;
      const img = new Image();
      img.src = u;
    });
  }, [index, photos, total, shareToken]);

  // Touch swipe handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const SWIPE_THRESHOLD = 50;
    if (distance > SWIPE_THRESHOLD) goNext();
    else if (distance < -SWIPE_THRESHOLD) goPrev();
  };

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="public-lightbox"
    >
      {/* Bouton fermer */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
        aria-label="Fermer"
        data-testid="public-lightbox-close"
      >
        <i className="bi bi-x-lg text-xl" />
      </button>

      {/* Compteur */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-semibold z-10" data-testid="public-lightbox-counter">
        {index + 1} / {total}
      </div>

      {/* Bouton précédent (desktop only) */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition z-10"
          aria-label="Photo précédente"
          data-testid="public-lightbox-prev"
        >
          <i className="bi bi-chevron-left text-xl" />
        </button>
      )}

      {/* Bouton suivant (desktop only) */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition z-10"
          aria-label="Photo suivante"
          data-testid="public-lightbox-next"
        >
          <i className="bi bi-chevron-right text-xl" />
        </button>
      )}

      {/* Photo avec chargement progressif (blur-up depuis thumb pendant que large charge) */}
      <ProgressiveImage
        src={url}
        lowSrc={getMediaUrl(photo, 'thumb', shareToken)}
        alt={photo.name || `Photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[88vh] md:max-w-[88vw] md:max-h-[85vh] object-contain rounded-lg"
        containerClassName="flex items-center justify-center"
        testId="public-lightbox-image"
      />

      {/* Caption mobile */}
      {photo.name && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs max-w-[80vw] text-center truncate">
          {photo.name}
        </div>
      )}

      {/* Hint swipe (mobile uniquement, masque dès 1er swipe) */}
      <div className="md:hidden absolute bottom-16 left-1/2 -translate-x-1/2 text-white/40 text-[10px] tracking-wide pointer-events-none">
        ← Glissez pour naviguer →
      </div>
    </div>
  );
};
